// React & Vendor Libs
const { useCallback } = wp.element;

// NekoUI
import { NekoBlock, NekoWrapper, NekoColumn, NekoSettings, NekoSelect,
  NekoOption, NekoButton, NekoInput, NekoTypo, NekoCheckboxGroup, NekoCheckbox } from '@neko-ui';

import { isPro } from '@app/settings';
import useDatabaseCleanerContext, { ages } from '../context';

const LogsStyles = {
  marginTop: 20,
  background: 'rgb(0, 72, 88)',
  padding: 10,
  color: 'rgb(58, 212, 58)',
  maxHeight: 400,
  minHeight: 200,
  display: 'block',
  fontFamily: 'monospace',
  fontSize: 12,
  whiteSpace: 'pre',
  overflowX: 'auto',
  borderRadius: 10
};

const SettingsSettings = () => {
  const { busy, busyAction, agaThreshold, bulkBatchSize, deepDeletions, hideMessage, delay } = useDatabaseCleanerContext();
  const { resetOptions, updateOption } = useDatabaseCleanerContext();
  const { fileContent, refreshLogs, clearLogs, generateFakeData, deleteCustomUsedBy } = useDatabaseCleanerContext();
  const { postTypesUsedBy, optionsUsedBy, tablesUsedBy, cronJobsUsedBy } = useDatabaseCleanerContext();

  const onChanged = useCallback(({ name, value }) => {
    updateOption({ param: { name, value } })
  }, [updateOption]);

  const onResetOptionsClick = async () => {
    await resetOptions();
  }

  const isBusyCustomUsedBy = busyAction.some(v => v.item === 'deleteCustomUsedBy');
  const isBusyLogs = busyAction.some(v => v.item === 'logs');

  return (
    <NekoWrapper>

      <NekoColumn minimal>

        <NekoBlock busy={busy} title="Settings" className="primary">
          <NekoSettings title="Age Threshold">
            <NekoSelect id="aga_threshold" scrolldown={true} onChange={(value, id) => onChanged({ name: id, value })}
              value={agaThreshold} description={<>Auto Clean will be applied on items which are older than the specified number of days, months or years.</>}>
              {ages.map((v) => <NekoOption id={v.value} value={v.value} label={v.label} />)}
            </NekoSelect>
          </NekoSettings>
          <NekoSettings title="Message">
            <NekoCheckboxGroup max="1">
              <NekoCheckbox id="hide_message" label="Hide" description="Have you read it twice? If yes, hide it :)" value="1" checked={hideMessage} onChange={(value, id) => onChanged({ name: id, value })} />
            </NekoCheckboxGroup>
          </NekoSettings>
        </NekoBlock>

        <NekoBlock busy={busy} title="Performance" className="primary">
          <NekoSettings title="Batch Size">
            <NekoInput id="bulk_batch_size" type="number" value={bulkBatchSize} onBlur={(value, id) => onChanged({ name: id, value })}
              description={<>The maximum amount of entries to delete at the same time. That will slow down the process a little but will make it work on huge databases.</>} />
          </NekoSettings>
          <NekoSettings title="Delay (in ms)">
            <NekoInput id="delay" type="number" value={delay} min="0" max="20000" onBlur={(value, id) => onChanged({ name: id, value })} description="Time to wait between each request (in milliseconds). The overall process is intensive so this gives the chance to your server to chill out a bit. A very good server doesn't need it, but a slow/shared hosting might even reject requests if they are too fast and frequent. Recommended value is actually 0, 100 for safety, 2000 or 5000 if your hosting is kind of cheap." />
          </NekoSettings>
          {/* <NekoSettings title="Deep Deletions">
            <NekoCheckboxGroup isPro={isPro}>
              <NekoCheckbox id="deep_deletions" label="Enable" requirePro onChange={(value, id) => onChanged({ name: id, value })}
                description={<>Items will be deleted by calling the internal WordPress functions, so it will have the side effect of a <i>natural cleaning</i>, as if you were deleting posts (and other elements) naturally.</>}
                checked={deepDeletions} />
            </NekoCheckboxGroup>
          </NekoSettings> */}
        </NekoBlock>

      </NekoColumn>

      <NekoColumn minimal>

        <NekoBlock busy={busy || isBusyCustomUsedBy} title={<>Custom <i>Used By</i> data</>} className="primary"
          action={<NekoButton color="#005883" busy={busy || isBusyCustomUsedBy} onClick={deleteCustomUsedBy}>Reset <i>Used By</i> data</NekoButton>}>
          {isPro && <NekoTypo>
            Did you categorized everything? Nice! ☺️ Please share this data with me through my <a href="https://meowapps.com/contact" target="_blank">contact form</a>. Thank you! 
          </NekoTypo>}
          <pre style={{ height: 145, overflow: 'auto', border: '1px solid #dedede', padding: 10,
              background: '#f8f8f8', color: '#8b8b8b' }}>
            {postTypesUsedBy?.length > 0 &&
              <>
                <b># POST TYPES</b>
                {postTypesUsedBy?.sort((a, b) => a.name.localeCompare(b.name))
                  .map(x => <div>{x.name}|{x.item}|{x.slug}</div>)}
                <br />
              </>
            }
            {optionsUsedBy?.length > 0 &&
              <>
                <b># OPTIONS</b>
                {optionsUsedBy?.sort((a, b) => a.name.localeCompare(b.name))
                  .map(x => <div>{x.name}|{x.item}|{x.slug}</div>)}
                <br />
              </>
            }
            {tablesUsedBy?.length > 0 &&
              <>
                <b># TABLES</b>
                {tablesUsedBy?.sort((a, b) => a.name.localeCompare(b.name))
                  .map(x => <div>{x.name}|{x.item}|{x.slug}</div>)}
                <br />
              </>
            }
            {cronJobsUsedBy?.length > 0 &&
              <>
                <b># CRON JOBS</b>
                {cronJobsUsedBy?.sort((a, b) => a.name.localeCompare(b.name))
                  .map(x => <div>{x.name}|{x.item}|{x.slug}</div>)}
                <br />
              </>
            }
          </pre>
        </NekoBlock>

        <NekoBlock title="Logs" busy={busy || isBusyLogs} className="primary"
          action={<div style={{ display: 'flex', alignItems: 'center' }}>
            <NekoButton color="#005883" onClick={clearLogs}>Clear Logs</NekoButton>
          </div>}>
          <NekoButton onClick={refreshLogs}>Refresh Logs</NekoButton>
          <div style={LogsStyles}>
            {fileContent}
          </div>
        </NekoBlock>

        <NekoBlock className="primary" title="For Advanced Users & Developers"
          busy={busy || isBusyLogs}>
          <NekoButton className="danger" disabled={busy} onClick={onResetOptionsClick}>
            Reset Options
          </NekoButton>
          <NekoButton className="secondary" onClick={generateFakeData}
            isBusy={busyAction.some(v => v.item === 'fakeData')}>
            Generate Fake Data
          </NekoButton>
        </NekoBlock>
      </NekoColumn>

    </NekoWrapper>
  );
};

export default SettingsSettings;